import { useCallback, useRef } from "react";

import { useAppDispatch } from "@/redux/hooks";

import { useLazyGetContactProspectDetailQuery } from "@/pages/Private/redux/contact/contact.api";
import { openContactLeadPanel } from "@/pages/Private/redux/contact/contact.slice";

import { OrganizationProfile } from "@/pages/Private/pages/List/schema/organization-profiles";

import { ContactProspectOrganization } from "../schema/contacts";
import { toProfileProspectPreview } from "../utils/toProfileProspectPreview";

/**
 * Points the open lead panel at a colleague from its own Profiles tab (PREC-4116).
 *
 * Same two steps as opening a lead from the global search: put the preview in the slice so the
 * panel re-renders on the new person straight away, then fetch that person's detail. The in-flight
 * fetch is aborted when a second colleague is clicked, so a slow first response cannot land after a
 * later one and overwrite the panel with the wrong lead.
 */
export const useRetargetLeadPanel = (): ((
	profile: OrganizationProfile,
	organization: ContactProspectOrganization | null
) => void) => {
	const dispatch = useAppDispatch();
	const [triggerGetContactProspectDetail] = useLazyGetContactProspectDetailQuery();
	const lastDetailQueryPromise = useRef<ReturnType<typeof triggerGetContactProspectDetail> | null>(
		null
	);

	return useCallback(
		(profile: OrganizationProfile, organization: ContactProspectOrganization | null) => {
			dispatch(openContactLeadPanel(toProfileProspectPreview(profile, organization)));
			lastDetailQueryPromise.current?.abort();
			lastDetailQueryPromise.current = triggerGetContactProspectDetail(profile.prospectId);
		},
		[dispatch, triggerGetContactProspectDetail]
	);
};
